// addMenuTypeFilters.js — which types appear under Records in add menus

import {
  typeAllowsManualCreate,
  typeAllowsTopLevelCreate,
} from 'utilities/typeCreatePolicy';
import { typeShowsInAddMenu } from 'utilities/typeDisplayPolicy';
import { typeHasAddMenuPresence } from 'utilities/typeAppearPolicy';
import { SKETCH_TYPE_KEY } from 'utilities/sketchType';
/** ClickUp system custom item ids excluded from Add menus (legacy connector catalog). */
function isClickUpSystemTaskType({ customItemId, label, name } = {}) {
  const id = Number(customItemId);
  if ([1, 7, 8].includes(id)) return true;
  const text = String(label || name || '').trim().toLowerCase();
  return text === 'milestone' || text === 'ai_skill' || text === 'form_response';
}

/** Legacy hardcoded vendor type keys (also matched via nativeManaged / connectorKey). */
export const VENDOR_PROVISIONED_TYPE_KEYS = Object.freeze(new Set([
  'CLICKUP_TASK',
]));

/** Types provisioned by connectors — show under vendor sections only. */
export function isVendorProvisionedType(typeDef) {
  if (!typeDef || typeof typeDef !== 'object') return false;
  if (typeDef.nativeManaged === true) return true;
  if (String(typeDef.connectorKey || '').trim()) return true;
  const key = String(typeDef.key || '').trim().toUpperCase();
  return VENDOR_PROVISIONED_TYPE_KEYS.has(key)
    || key.startsWith('CLICKUP_');
}

/**
 * Capture types pinned above the generic + Add list on node Add.
 * Event is also a normal add-menu type (showInAddMenu); Note / Sketch stay
 * pinned-only unless Show in Add menu is checked.
 */
export const PINNED_NOTE_TYPE_KEY = 'NOTE';
export const PINNED_SKETCH_TYPE_KEY = SKETCH_TYPE_KEY;
export const PINNED_EVENT_TYPE_KEY = 'EVENT';

/** Fallback when the base has not provisioned a NOTE type doc yet. */
export const PINNED_NOTE_FALLBACK = Object.freeze({
  key: PINNED_NOTE_TYPE_KEY,
  label: 'Note',
  iconKey: 'sticky_note_2',
});

/** Fallback when the base has not provisioned an EVENT type doc yet. */
export const PINNED_EVENT_FALLBACK = Object.freeze({
  key: PINNED_EVENT_TYPE_KEY,
  label: 'Event',
  iconKey: 'event',
});

/** Fixed add-menu action label (parallel to Take photo / Schedule Event). */
export const PINNED_NOTE_MENU_LABEL = 'Notes';

/** Fixed add-menu action label (parallel to Notes). */
export const PINNED_SKETCH_MENU_LABEL = 'Sketch';

/** Fixed add-menu action label for scheduling related events. */
export const PINNED_EVENT_MENU_LABEL = 'Event';

function normalizeKey(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * Resolves the pinned Note row for add menus.
 * Always returns a def so Note appears even before the type is scaffolded.
 *
 * @param {Array<object>} [typeDefs=[]] - Base node type definitions.
 * @returns {{ key: string, label: string, iconKey: string }}
 */
export function resolvePinnedNoteType(typeDefs = []) {
  const found = (typeDefs || []).find(
    (typeDef) => normalizeKey(typeDef?.key) === PINNED_NOTE_TYPE_KEY,
  );
  if (!found) return { ...PINNED_NOTE_FALLBACK };

  return {
    key: PINNED_NOTE_TYPE_KEY,
    label: String(found.label || PINNED_NOTE_FALLBACK.label).trim() || PINNED_NOTE_FALLBACK.label,
    iconKey: String(found.iconKey || PINNED_NOTE_FALLBACK.iconKey).trim() || PINNED_NOTE_FALLBACK.iconKey,
  };
}

/**
 * Resolves the pinned Sketch row for add menus.
 *
 * @param {Array<object>} [typeDefs=[]] - Base node type definitions.
 * @returns {{ key: string, label: string, iconKey: string }|null}
 */
export function resolvePinnedSketchType(typeDefs = []) {
  const found = (typeDefs || []).find(
    (typeDef) => normalizeKey(typeDef?.key) === PINNED_SKETCH_TYPE_KEY,
  );
  if (!found) return null;

  return {
    key: PINNED_SKETCH_TYPE_KEY,
    label: String(found.label || PINNED_SKETCH_MENU_LABEL).trim() || PINNED_SKETCH_MENU_LABEL,
    iconKey: String(found.iconKey || 'draw').trim() || 'draw',
  };
}

/**
 * Resolves the pinned Event row for add menus.
 * Always returns a def so Event appears even before the type is scaffolded.
 *
 * @param {Array<object>} [typeDefs=[]] - Base node type definitions.
 * @returns {{ key: string, label: string, iconKey: string }}
 */
export function resolvePinnedEventType(typeDefs = []) {
  const found = (typeDefs || []).find(
    (typeDef) => normalizeKey(typeDef?.key) === PINNED_EVENT_TYPE_KEY,
  );
  if (!found) return { ...PINNED_EVENT_FALLBACK };

  return {
    key: PINNED_EVENT_TYPE_KEY,
    label: String(found.label || PINNED_EVENT_FALLBACK.label).trim() || PINNED_EVENT_FALLBACK.label,
    iconKey: String(found.iconKey || PINNED_EVENT_FALLBACK.iconKey).trim() || PINNED_EVENT_FALLBACK.iconKey,
  };
}

/**
 * Returns blank-creatable record types (excludes vendor / ingest-only / Show in
 * Add menu off). Does not apply top-level create policy — use getRecordMenuTypes
 * for global New.
 *
 * @param {Array<object>} typeDefs - Base node type definitions.
 * @returns {Array<object>} Sorted type defs for menu rows.
 */
function getCreatableRecordMenuTypes(typeDefs = []) {
  return (typeDefs || [])
    .filter((typeDef) => {
      const key = normalizeKey(typeDef?.key);
      if (!key) return false;
      if (isVendorProvisionedType(typeDef)) return false;
      if (!typeHasAddMenuPresence(typeDef)) return false;
      return typeAllowsManualCreate(typeDef);
    })
    .sort((a, row) => String(a?.label || a?.key || '').localeCompare(String(row?.label || row?.key || '')));
}

/**
 * Returns record types eligible for global "+ Add".
 * Child-only types (line items) are excluded via allowTopLevelCreate.
 *
 * @param {Array<object>} typeDefs - Base node type definitions.
 * @returns {Array<object>} Sorted type defs for menu rows.
 */
export function getRecordMenuTypes(typeDefs = []) {
  return getCreatableRecordMenuTypes(typeDefs)
    .filter((typeDef) => typeAllowsTopLevelCreate(typeDef));
}

/** Media type keys that unlock the Upload files action when related types are enforced. */
const RELATED_UPLOAD_TYPE_KEYS = Object.freeze(new Set([
  'PHOTO',
  'VIDEO',
  'DOCUMENT',
  'FILE',
  'RECORDING',
  'SNAPSHOT',
]));

/**
 * Normalizes a related-type allowlist to uppercase keys.
 *
 * @param {string[]|null|undefined} relatedTypeKeys - Host type suggestedChildren.
 * @returns {string[]|null} Uppercase keys, or null when enforcement is off.
 */
export function normalizeRelatedTypeKeys(relatedTypeKeys) {
  if (!Array.isArray(relatedTypeKeys)) return null;
  const seen = new Set();
  const next = [];
  relatedTypeKeys.forEach((entry) => {
    const key = normalizeKey(entry);
    if (!key || seen.has(key)) return;
    seen.add(key);
    next.push(key);
  });
  return next;
}

/**
 * Returns whether a type/action key is allowed by a related-type allowlist.
 *
 * @param {string[]|null|undefined} relatedTypeKeys - Host type suggestedChildren.
 * @param {string} typeKey - Candidate type or synthetic media action key.
 * @returns {boolean}
 */
export function isRelatedTypeAllowed(relatedTypeKeys, typeKey) {
  const allowed = normalizeRelatedTypeKeys(relatedTypeKeys);
  if (!allowed) return true;

  const normalized = normalizeKey(typeKey);
  if (!normalized) return false;

  if (normalized === 'CAMERA') {
    return allowed.includes('PHOTO');
  }
  if (normalized === 'RECORDING') {
    return allowed.includes('VIDEO') || allowed.includes('RECORDING');
  }
  if (normalized === 'ATTACH') {
    return allowed.some((key) => RELATED_UPLOAD_TYPE_KEYS.has(key));
  }

  if (normalized === PINNED_EVENT_TYPE_KEY && allowed.includes(PINNED_NOTE_TYPE_KEY)) {
    return true;
  }

  if (normalized === PINNED_SKETCH_TYPE_KEY && allowed.includes(PINNED_NOTE_TYPE_KEY)) {
    return true;
  }

  return allowed.includes(normalized);
}

/**
 * Visibility flags for pinned Note / Event / photo actions when related types are enforced.
 *
 * @param {string[]|null|undefined} relatedTypeKeys - Host type suggestedChildren.
 * @returns {{
 *   showNote: boolean,
 *   showSketch: boolean,
 *   showEvent: boolean,
 *   showPhoto: boolean,
 *   showVideo: boolean,
 *   showUpload: boolean,
 * }}
 */
export function getRelatedMediaActionVisibility(relatedTypeKeys) {
  const allowed = normalizeRelatedTypeKeys(relatedTypeKeys);
  if (!allowed) {
    return {
      showNote: true,
      showSketch: true,
      showEvent: true,
      showPhoto: true,
      showVideo: true,
      showUpload: true,
    };
  }

  const allowedSet = new Set(allowed);
  const showNote = allowedSet.has(PINNED_NOTE_TYPE_KEY);
  return {
    showNote,
    showSketch: allowedSet.has(PINNED_SKETCH_TYPE_KEY) || showNote,
    // Offer Schedule Event alongside Add Note when Notes are related,
    // or when EVENT itself is explicitly allowed.
    showEvent: allowedSet.has(PINNED_EVENT_TYPE_KEY) || showNote,
    showPhoto: allowedSet.has('PHOTO'),
    showVideo: allowedSet.has('VIDEO') || allowedSet.has('RECORDING'),
    showUpload: allowed.some((key) => RELATED_UPLOAD_TYPE_KEYS.has(key)),
  };
}

/**
 * Returns record types for the node Add menu, optionally constrained/ordered by
 * the host type's related types (`suggestedChildren`).
 *
 * @param {Array<object>} typeDefs - Base node type definitions.
 * @param {string[]|null|undefined} [relatedTypeKeys] - Host type suggestedChildren.
 * @returns {Array<object>} Type defs for menu rows.
 */
export function getRelatedRecordMenuTypes(typeDefs = [], relatedTypeKeys) {
  // Related pickers may include child-only types (e.g. Proposal Item).
  const recordTypes = getCreatableRecordMenuTypes(typeDefs);
  const allowed = normalizeRelatedTypeKeys(relatedTypeKeys);
  if (!allowed) return recordTypes;

  const byKey = new Map(
    recordTypes.map((typeDef) => [normalizeKey(typeDef?.key), typeDef]),
  );

  return allowed
    .map((typeKey) => byKey.get(typeKey))
    .filter(Boolean);
}

/**
 * ClickUp / native types that support origin create (Add menu vendor section).
 *
 * @param {Array<object>} [typeDefs=[]]
 * @param {string} [connectorKey='clickup']
 * @returns {Array<object>}
 */
export function getNativeCreatableTypes(typeDefs = [], connectorKey = 'clickup') {
  const wanted = String(connectorKey || '').trim().toLowerCase();
  return (typeDefs || [])
    .filter((typeDef) => {
      const connector = String(typeDef?.connectorKey || '').trim().toLowerCase();
      const key = String(typeDef?.key || '').trim().toLowerCase();
      if (!key) return false;
      const matchesConnector = connector === wanted
        || (wanted === 'clickup' && key.startsWith('clickup_'));
      if (!matchesConnector) return false;
      if (typeDef?.originCrud?.create === false) return false;
      // Prefer explicit origin CRUD / allowManualCreate; still include nativeManaged
      // clickup_* even when an older allowManualCreate:false stamp remains.
      if (typeDef?.originCrud?.create === true) return true;
      if (typeDef?.allowManualCreate === true) return true;
      if (typeDef?.nativeManaged === true) return true;
      return typeAllowsManualCreate(typeDef);
    })
    .sort((a, row) => String(a?.label || a?.key || '').localeCompare(String(row?.label || row?.key || '')));
}

/**
 * ClickUp Add-menu rows: prefer the connection's Task Type catalog so types
 * exist in ClickUp (Lead, Project, …) show even before Appdeck has provisioned
 * a type doc from pull/create. Fall back to provisioned typeDefs when catalog
 * is empty.
 *
 * @param {{ typeDefs?: object[], source?: object|null, connectorKey?: string }} [params]
 * @returns {Array<{ key: string, label: string, iconKey: string, originCustomItemId: number }>}
 */
export function getClickUpCreatableMenuTypes({
  typeDefs = [],
  source = null,
  connectorKey = 'clickup',
} = {}) {
  const provisioned = getNativeCreatableTypes(typeDefs, connectorKey);
  const byKey = new Map(
    provisioned.map((row) => [String(row?.key || '').trim().toLowerCase(), row]),
  );

  const catalog = Array.isArray(source?.scope?.taskTypeCatalog)
    ? source.scope.taskTypeCatalog
    : [];

  if (catalog.length) {
    const rows = [];
    const seen = new Set();
    catalog.forEach((row) => {
      if (isClickUpSystemTaskType({
        customItemId: row?.customItemId,
        label: row?.label,
        name: row?.label,
      })) return;
      const key = String(row?.nativeTypeKey || row?.key || '').trim();
      if (!key) return;
      const lower = key.toLowerCase();
      if (seen.has(lower)) return;
      seen.add(lower);
      const fromType = byKey.get(lower);
      rows.push({
        key,
        label: String(row?.label || fromType?.label || key).trim() || key,
        iconKey: fromType?.iconKey || 'task_alt',
        originCustomItemId: Number(row?.customItemId) || 0,
      });
    });
    // Keep Task first, then alphabetical — matches create-modal expectations.
    return rows.sort((a, b) => {
      const aTask = (Number(a.originCustomItemId) || 0) === 0 ? 0 : 1;
      const bTask = (Number(b.originCustomItemId) || 0) === 0 ? 0 : 1;
      if (aTask !== bTask) return aTask - bTask;
      return String(a.label).localeCompare(String(b.label));
    });
  }

  return provisioned
    .map((typeDef) => ({
      key: String(typeDef?.key || '').trim(),
      label: String(typeDef?.label || typeDef?.key || '').trim(),
      iconKey: typeDef?.iconKey || 'task_alt',
      originCustomItemId: Number(typeDef?.originCustomItemId) || 0,
    }))
    .filter((row) => (
      row.key
      && !isClickUpSystemTaskType({
        customItemId: row.originCustomItemId,
        label: row.label,
      })
    ));
}

/**
 * Returns true when the current user owns the OAuth connection for a source row.
 *
 * @param {object} source - Source instance row.
 * @param {string} userKey - Current user key.
 * @returns {boolean}
 */
export function isSourceOwnedByUser(source, userKey) {
  const owner = String(source?.connectedByUserKey || '').trim();
  const user = String(userKey || '').trim();
  if (!owner || !user) return false;
  return owner === user;
}

/**
 * Returns live source rows owned by the current user (not purging).
 *
 * @param {Array<object>} sources
 * @param {string} userKey
 * @returns {Array<object>}
 */
export function getUserOwnedSources(sources = [], userKey = '') {
  return (sources || []).filter((row) => {
    if (String(row?.status || '').trim() === 'purging') return false;
    if (row?.enabled === false) return false;
    return isSourceOwnedByUser(row, userKey);
  });
}

/**
 * Returns true when a source can offer Attach… (enabled, not purging, has pulled nodes).
 * Base-scoped — any connection on the base, not only the current user's OAuth.
 *
 * @param {object} source - Source instance row.
 * @returns {boolean}
 */
export function isSourceAttachable(source) {
  if (!source) return false;
  if (String(source?.status || '').trim() === 'purging') return false;
  if (source?.enabled === false) return false;
  return Number(source?.pulledCount) > 0;
}

/**
 * Returns attachable source rows on the base (any connector owner).
 *
 * @param {Array<object>} sources
 * @returns {Array<object>}
 */
export function getAttachableSources(sources = []) {
  return (sources || []).filter(isSourceAttachable);
}

/**
 * Returns true when a node belongs to the given connector source type.
 *
 * @param {object} node
 * @param {string} sourceType
 * @returns {boolean}
 */
export function nodeMatchesSourceType(node, sourceType) {
  const wanted = String(sourceType || '').trim().toLowerCase();
  if (!wanted) return true;

  const actual = String(node?.sourceType || '').trim().toLowerCase();
  if (actual === wanted) return true;

  if (wanted === 'calendar' && node?.data_calendar) return true;
  if (wanted === 'drive' && node?.data_drive) return true;
  if (wanted === 'gmail' && (node?.data_email_thread || node?.data_email)) return true;
  if (wanted === 'clickup' && node?.data_clickup_task) return true;
  if (wanted === 'dropbox' && node?.data_dropbox) return true;

  return false;
}
